import { memo, useCallback, useMemo, useRef, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { EnrichmentRunState, ProspectBookFilter } from '@signalvote/shared';
import { Skeleton } from '@/components/ui/skeleton';
import {
  useAddToCallSheet,
  useCreateCallSheet,
  useEnrichmentRun,
  useFundraisingTabCounts,
  usePrefetchProspectDetail,
  useProspectBook,
  useProspectListNames,
  useUpdateProspectNotes,
} from '@/hooks/queries';
import { bookQuery, narrowingChangesCleared, narrowingNames, withFilters, type BookFilters } from '@/lib/fundraising/bookFilters';
import { bookSortChanges, bookSortOf } from '@/lib/fundraising/sorting';
import { BookNarrowingBar } from './BookNarrowingBar';
import { EnrichmentWork } from './EnrichmentWork';
import { FundraisingPage } from './FundraisingPage';
import { ProspectBookList, type RowNote, type RowNoteSave } from './ProspectBookList';
import { keptHistoryState, openedFromList, ProspectProfileModal } from './ProspectProfileModal';

const BOOK = '/admin/fundraising';
const NO_LISTS: never[] = [];

/**
 * The Prospects tab: the book, with importing people (and matching them to federal giving) in the header
 * and above the list. The segment, the Match filter, a call book, an uploaded list, a place, an Ask range
 * and the search all narrow the book together,
 * and live in the URL (`filters`). A person opens over the book in their profile (`person`, also in the URL, so
 * Back closes them and a link reopens them), stepping through the rows as filtered; the book stays put behind.
 */
export function ProspectsBook({
  filters,
  person,
  onFiltersChange,
}: {
  filters: BookFilters;
  /** The person open over the book, if any. */
  person?: string;
  onFiltersChange: (next: BookFilters) => void;
}) {
  const navigate = useNavigate();
  const open = useCallback(
    (id: string) => void navigate({ to: BOOK, search: { ...filters, person: id }, state: openedFromList }),
    [navigate, filters],
  );
  // The same read as the book (one cache entry): its rows as filtered are what J / K step through.
  const { data: book } = useProspectBook(bookQuery(filters));
  const order = useMemo(() => (book?.rows ?? []).map((row) => row.prospect.id), [book?.rows]);
  return (
    <>
      <BookPage filters={filters} onFiltersChange={onFiltersChange} onOpen={open} />
      {person && (
        <ProspectProfileModal
          prospectId={person}
          order={order}
          total={book?.total ?? 0}
          sheets={book?.sheets ?? []}
          onStep={(id) => void navigate({ to: BOOK, search: { ...filters, person: id }, replace: true, state: keptHistoryState })}
          onDismiss={() => void navigate({ to: BOOK, search: filters, replace: true })}
        />
      )}
    </>
  );
}

/**
 * The book itself. Memoized, so opening someone or stepping through people redraws only the dialog, never
 * the book behind it — which can be thousands of rows long.
 */
const BookPage = memo(function BookPage({
  filters,
  onFiltersChange,
  onOpen,
}: {
  filters: BookFilters;
  onFiltersChange: (next: BookFilters) => void;
  onOpen: (prospectId: string) => void;
}) {
  const navigate = useNavigate();
  const prefetch = usePrefetchProspectDetail();
  const [actionSlot, setActionSlot] = useState<HTMLDivElement | null>(null);
  const query = bookQuery(filters);
  const { activity, match, sheetId, search } = query;
  // Several changes in one handler ("Clear" clears each filter in turn) build on each other, not on the
  // filters this render was given — each would otherwise undo the one before it.
  const latest = useRef(filters);
  latest.current = filters;
  const change = (changes: Parameters<typeof withFilters>[1]) => {
    latest.current = withFilters(latest.current, changes);
    onFiltersChange(latest.current);
  };
  // While a changed filter, search or sort loads, the last book stays on screen, marked stale.
  const { data: book, isLoading, isError, isPlaceholderData } = useProspectBook(query);
  const { data: lists = NO_LISTS } = useProspectListNames();
  // The banners speak for the whole book, whatever is filtered — the same read as the tab counts (the book's own
  // counts when nothing filters it).
  const { data: whole } = useFundraisingTabCounts(query);
  const { data: run } = useEnrichmentRun();
  const { mutate: createSheet, error: createError, reset: resetCreate } = useCreateCallSheet();
  const { mutate: addToSheet, error: addError, reset: resetAdd } = useAddToCallSheet();
  const sheetError = (createError ?? addError)?.message ?? null;
  const { mutate: saveNotes, error: noteError, isPending: savingNote, variables: noteSent, reset: resetNote } = useUpdateProspectNotes();
  const noteSave: RowNoteSave | null = noteSent
    ? { prospectId: noteSent.id, saving: savingNote, error: noteError?.message ?? null }
    : null;
  // Stable, so writing one person's note re-renders only their row.
  const saveNote = useCallback(
    ({ prospectId, notes, concurrencyVersion }: RowNote, onSaved: () => void) => {
      resetNote();
      saveNotes({ id: prospectId, notes, concurrencyVersion }, { onSuccess: onSaved });
    },
    [saveNotes, resetNote],
  );
  const resetSheetErrors = () => {
    resetCreate();
    resetAdd();
  };
  const changeSheet = (next: string | null) => {
    resetSheetErrors();
    change({ sheet: next });
  };
  const showEveryone = () => {
    latest.current = {};
    onFiltersChange({});
  };

  // Nobody in the book at all: the page is the upload itself instead of an empty list.
  const emptyBook = whole?.counts.everyone === 0;

  return (
    <FundraisingPage tab="prospects" actionRef={setActionSlot} book={query}>
      <div className="space-y-4">
        <EnrichmentWork
          counts={whole?.counts}
          searching={search.trim() !== ''}
          emptyBook={emptyBook}
          actionSlot={actionSlot}
          onReviewPossible={() => void navigate({ to: '/admin/fundraising/match-review' })}
          onSeeEveryone={showEveryone}
          onShowUnmatched={() => {
            // Every one of them, whatever else was filtered.
            latest.current = { match: ProspectBookFilter.NotMatchedYet };
            onFiltersChange(latest.current);
          }}
        />

        {isLoading && (
          <div className="space-y-2">
            {[0, 1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-14 w-full" />
            ))}
          </div>
        )}
        {isError && !isLoading && (
          <p className="text-sm text-muted-foreground">
            {sheetId ? (
              <>
                We couldn&apos;t load that call book — it may have been deleted.{' '}
                <button type="button" className="underline" onClick={() => changeSheet(null)}>
                  Back to everyone
                </button>
              </>
            ) : (
              'We couldn’t load the prospects right now.'
            )}
          </p>
        )}
        {book && !emptyBook && (
          <ProspectBookList
            book={book}
            activity={activity}
            match={match}
            search={search}
            onActivityChange={(next) => change({ activity: next })}
            onMatchChange={(next) => change({ match: next })}
            onSearchChange={(next) => change({ q: next })}
            onOpen={onOpen}
            onHover={prefetch}
            onCreateSheet={(name, prospectIds, onDone) => {
              resetSheetErrors();
              createSheet({ name, prospectIds }, { onSuccess: onDone });
            }}
            onAddToSheet={(sheet, prospectIds, onDone) => {
              resetSheetErrors();
              addToSheet({ sheet, prospectIds }, { onSuccess: onDone });
            }}
            sheetError={sheetError}
            sheetId={sheetId}
            onSheetChange={changeSheet}
            matching={run?.state === EnrichmentRunState.Running}
            moreFilters={<BookNarrowingBar filters={filters} lists={lists} askCeiling={book.askCeiling} onChange={change} />}
            narrowingNames={narrowingNames(filters, lists)}
            onClearNarrowing={() => change(narrowingChangesCleared)}
            onSaveNote={saveNote}
            noteSave={noteSave}
            // Narrowed to a call book with no column picked, the rows come in that book's own order.
            sort={filters.sheet && !filters.sort && !filters.dir ? null : bookSortOf(filters)}
            onSortChange={(next) => change(bookSortChanges(next))}
            stale={isPlaceholderData}
          />
        )}
      </div>
    </FundraisingPage>
  );
});
